import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { SquaresFour, CalendarBlank, PencilLine, Tray, DeviceMobile, ChatsCircle, ChartLineUp, Images, Article, CreditCard, Sparkle, GearSix, List, X, SignOut, Lightning, ListChecks, LinkSimple, Binoculars, Question } from "@phosphor-icons/react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import GuidedTour from "./GuidedTour";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank },
  { to: "/composer", label: "Composer", icon: PencilLine },
  { to: "/approvals", label: "Approvals", icon: Tray },
  { to: "/queue", label: "Ready-to-Post", icon: DeviceMobile },
  { to: "/engage", label: "Engage", icon: ChatsCircle },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/insights", label: "Insights", icon: ChartLineUp },
  { to: "/assets", label: "Assets", icon: Images },
  { to: "/seo", label: "SEO Writer", icon: Article },
  { to: "/bio", label: "Link-in-Bio", icon: LinkSimple },
  { to: "/research", label: "Research", icon: Binoculars },
  { to: "/brand", label: "Brand Intelligence", icon: Sparkle },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: GearSix },
];

const ROLE_HIDE = {
  operator: ["/billing", "/settings"],
  designer: ["/approvals", "/queue", "/engage", "/insights", "/seo", "/research", "/billing", "/settings"],
  translator: ["/approvals", "/queue", "/engage", "/insights", "/seo", "/assets", "/bio", "/research", "/brand", "/billing", "/settings"],
};

export default function AppShell({ children }) {
  const { user, org, clients, activeClientId, setActiveClientId, logout } = useApp();
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState(org?.credits ?? 0);
  const [tourOpen, setTourOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("celebre_tour_done") && window.innerWidth >= 1024) {
      const t = setTimeout(() => setTourOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);
  const closeTour = () => { localStorage.setItem("celebre_tour_done", "1"); setTourOpen(false); };

  useEffect(() => { setCredits(org?.credits ?? 0); }, [org]);
  useEffect(() => {
    const iv = setInterval(async () => {
      try { const { data } = await api.get("/credits"); setCredits(data.balance); } catch { /* ignore */ }
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="min-h-screen bg-[hsl(30,33%,98%)]">
      <aside className={`fixed inset-y-0 left-0 z-40 w-60 bg-white border-r border-slate-200/80 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="h-14 px-5 flex items-center justify-between border-b border-slate-100">
          <button data-testid="logo-home-link" onClick={() => navigate("/dashboard")} className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-[hsl(9,69%,62%)] flex items-center justify-center"><Lightning size={14} weight="fill" color="#fff" /></span>
            <span className="font-display font-bold text-[17px] tracking-tight">Celebre</span>
          </button>
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.filter((n) => !(ROLE_HIDE[user?.role] || []).includes(n.to)).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${isActive ? "bg-[hsl(9,69%,62%)]/8 text-[hsl(9,55%,48%)]" : "text-slate-600 hover:bg-orange-50/60 hover:text-slate-900"}`}>
              <Icon size={17} weight="duotone" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <button data-testid="logout-button" onClick={logout} className="flex w-full items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-30 h-14 glass-bar border-b border-black/5 flex items-center gap-3 px-4 sm:px-6">
          <button data-testid="mobile-menu-button" className="lg:hidden text-slate-500" onClick={() => setOpen(true)}><List size={20} /></button>
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-[11px] uppercase tracking-[0.2em] text-slate-400 font-medium">Client</span>
            <Select value={activeClientId} onValueChange={setActiveClientId}>
              <SelectTrigger data-testid="client-switcher" className="h-8 w-[170px] sm:w-[200px] text-[13px] bg-white border-slate-200">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.client_id} value={c.client_id} data-testid={`client-option-${c.client_id}`}>
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button data-testid="tour-launch-button" onClick={() => setTourOpen(true)} title="Take the tour"
              className="w-8 h-8 rounded-full border border-orange-200/70 bg-orange-50/60 text-[hsl(9,55%,48%)] flex items-center justify-center hover:bg-orange-100/80 transition-colors">
              <Question size={16} weight="bold" />
            </button>
            <button data-testid="credit-balance-badge" onClick={() => navigate("/billing")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(9,69%,62%)]/10 border border-[hsl(9,69%,62%)]/25 text-[hsl(9,55%,46%)] text-[12px] font-semibold hover:bg-[hsl(9,69%,62%)]/15 transition-colors">
              <Lightning size={13} weight="fill" /> {credits} credits
            </button>
            <div data-testid="user-avatar" className="w-8 h-8 rounded-full bg-[#2F2D2C] text-white flex items-center justify-center text-[12px] font-semibold" title={`${user?.name} · ${user?.role}`}>
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">{children}</main>
      </div>
      {open && <div className="fixed inset-0 z-30 bg-black/20 lg:hidden" onClick={() => setOpen(false)} />}
      <GuidedTour open={tourOpen} onClose={closeTour} />
    </div>
  );
}
